import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RotateCcw } from "lucide-react";

/** Dev-mode banner showing the OTP (visible only when backend returns dev_otp). */
function DevOtpBanner({ devOtp }) {
    if (!devOtp) return null;
    return (
        <div
            data-testid="login-dev-otp"
            className="mt-2 rounded-xl bg-[#F57C00]/10 border border-[#F57C00]/30 px-4 py-3 text-[13px] text-[#1F1F1F]"
        >
            <span className="font-semibold text-[#F57C00]">DEV MODE:</span> Your OTP is{" "}
            <span className="font-bold tracking-wider">{devOtp}</span> (auto-filled in
            production this will be SMS only).
        </div>
    );
}

/** Step 2 of login: 6-digit OTP entry with dev banner, resend and change-number. */
export function OtpStep({
    t,
    code,
    setCode,
    devOtp,
    verifying,
    sending,
    secondsLeft,
    resendLeft,
    onSubmit,
    onChangeNumber,
    onResend,
    accountPreview,
}) {
    // Resend unlocks after a short 30s cooldown; falls back to the full expiry
    // timer only when the caller doesn't provide resendLeft.
    const resendWait = typeof resendLeft === "number" ? resendLeft : secondsLeft;
    return (
        <form onSubmit={onSubmit} className="space-y-5">
            {accountPreview?.first_name && (
                <div
                    data-testid="login-account-preview"
                    className="flex items-center gap-3 rounded-2xl bg-[#1B5E20]/5 border border-[#1B5E20]/10 px-4 py-3"
                >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1B5E20] text-white text-[18px] font-bold overflow-hidden shrink-0">
                        {accountPreview.avatar ? (
                            <img src={accountPreview.avatar} alt="" className="h-12 w-12 object-cover" />
                        ) : (
                            accountPreview.first_name[0]
                        )}
                    </span>
                    <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-[#1B5E20]">
                            Welcome back, {accountPreview.first_name} 🙏
                        </div>
                        <div className="text-[12px] text-[#4A5D4E]">Enter your OTP to continue.</div>
                    </div>
                </div>
            )}
            <div className="space-y-2">
                <Label htmlFor="otp" className="text-[#1F1F1F] text-[15px] font-medium">
                    {t("otpLabel")}
                </Label>
                <Input
                    data-testid="login-otp-input"
                    id="otp"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="••••••"
                    className="h-16 bg-white border-[#E5E5E5] text-center text-[28px] tracking-[0.6em] font-semibold"
                />
                {devOtp && <DevOtpBanner devOtp={devOtp} />}
            </div>

            <Button
                type="submit"
                data-testid="login-verify-otp-btn"
                disabled={verifying || code.length !== 6}
                className="w-full h-14 rounded-xl bg-[#1B5E20] hover:bg-[#154d1a] text-white text-[16px] font-semibold"
            >
                {verifying ? t("verifying") : t("verifyContinue")}
            </Button>

            <div className="flex items-center justify-between text-[14px]">
                <button
                    type="button"
                    data-testid="login-change-number"
                    onClick={onChangeNumber}
                    className="text-[#1B5E20] font-medium hover:underline"
                >
                    {t("changeNumber")}
                </button>
                <button
                    type="button"
                    data-testid="login-resend-otp"
                    disabled={resendWait > 0 || sending}
                    onClick={onResend}
                    aria-live="polite"
                    className="inline-flex items-center gap-1.5 text-[#F57C00] font-medium disabled:opacity-50 hover:underline"
                >
                    <RotateCcw className="h-4 w-4" />
                    {resendWait > 0 ? `${t("resendIn")} ${resendWait}s` : t("resendOtp")}
                </button>
            </div>
        </form>
    );
}
